export const PLANNING_TOOL_KEYS = [
  'whiteboard',
  'todos',
  'notes',
  'plot',
  'timeline',
  'hierarchies',
  'maps',
  'elements-character',
  'elements-location',
  'elements-object',
  'elements-species',
  'elements-creature',
  'elements-organization',
  'elements-graph',
] as const

export type PlanningToolKey = (typeof PLANNING_TOOL_KEYS)[number]

export const DEFAULT_PLANNING_TOOL: PlanningToolKey = 'todos'

export const EXPORTER_MODE_KEYS = ['quick', 'maquetador', 'import'] as const
export type ExporterModeKey = (typeof EXPORTER_MODE_KEYS)[number]
export const DEFAULT_EXPORTER_MODE: ExporterModeKey = 'quick'

export const ANALYSIS_VIEW_KEYS = ['overview', 'detail', 'compare'] as const
export type AnalysisViewKey = (typeof ANALYSIS_VIEW_KEYS)[number]
export const DEFAULT_ANALYSIS_VIEW: AnalysisViewKey = 'overview'

export const ANALYSIS_DETAIL_TAB_KEYS = [
  'dashboard',
  'general',
  'words',
  'paragraphs',
  'text',
] as const
export type AnalysisDetailTabKey = (typeof ANALYSIS_DETAIL_TAB_KEYS)[number]
export const DEFAULT_ANALYSIS_DETAIL_TAB: AnalysisDetailTabKey = 'dashboard'

/** Query keys owned by each project tab (excluding `tab` itself). */
export const TAB_QUERY_KEYS: Record<string, readonly string[]> = {
  editor: ['chapterId', 'detail'],
  concentration: [],
  planning: ['tool', 'elementId', 'folderId', 'hierarchyId', 'timelineId', 'whiteboardId'],
  snapshots: ['snapshotId'],
  exporter: ['mode'],
  analysis: ['view', 'analysisId', 'chapterId', 'compareChapterIds', 'detailTab'],
}

/** Planning keys that only apply for specific tools. Cleared when leaving that tool. */
export const PLANNING_CONTEXT_KEYS = [
  'elementId',
  'folderId',
  'hierarchyId',
  'timelineId',
  'whiteboardId',
] as const

export function isPlanningToolKey(value: string): value is PlanningToolKey {
  return (PLANNING_TOOL_KEYS as readonly string[]).includes(value)
}

export function isNarrativeElementTool(tool: string): boolean {
  return tool.startsWith('elements-')
}

export function isExporterModeKey(value: string): value is ExporterModeKey {
  return (EXPORTER_MODE_KEYS as readonly string[]).includes(value)
}

export function isAnalysisViewKey(value: string): value is AnalysisViewKey {
  return (ANALYSIS_VIEW_KEYS as readonly string[]).includes(value)
}

export function isAnalysisDetailTabKey(value: string): value is AnalysisDetailTabKey {
  return (ANALYSIS_DETAIL_TAB_KEYS as readonly string[]).includes(value)
}

/**
 * Build a planning slice patch for a tool change: set `tool` and clear context
 * keys that do not apply to the new tool.
 */
export function planningSliceForTool(
  tool: PlanningToolKey,
): Record<string, string | null> {
  const patch: Record<string, string | null> = { tool }

  if (!isNarrativeElementTool(tool)) {
    patch.elementId = null
    patch.folderId = null
  }
  if (tool !== 'hierarchies') {
    patch.hierarchyId = null
  }
  if (tool !== 'timeline') {
    patch.timelineId = null
  }
  if (tool !== 'whiteboard') {
    patch.whiteboardId = null
  }

  return patch
}

export function serializeIdList(ids: string[]): string {
  return [...new Set(ids.map((id) => id.trim()).filter(Boolean))].join(',')
}

export function parseIdList(value: string): string[] {
  if (!value) return []
  return [...new Set(value.split(',').map((id) => id.trim()).filter(Boolean))]
}

export function normalizeProjectTab(rawTab: unknown): string {
  const tab = Array.isArray(rawTab) ? rawTab[0] : rawTab
  const value = tab != null && tab !== '' ? String(tab) : 'editor'
  // Legacy plot/tools tabs redirect to planning
  if (value === 'plot' || value === 'tools') {
    return 'planning'
  }
  return value
}

export function queryValueToString(value: unknown): string {
  if (value == null) return ''
  if (Array.isArray(value)) {
    return value[0] != null ? String(value[0]) : ''
  }
  return String(value)
}

export function routeQueryToRecord(
  query: Record<string, unknown>,
): Record<string, string> {
  const result: Record<string, string> = {}
  Object.keys(query).forEach((key) => {
    const value = queryValueToString(query[key])
    if (value !== '') {
      result[key] = value
    }
  })
  return result
}

export function pickTabSlice(
  query: Record<string, unknown>,
  tab: string,
): Record<string, string> {
  const keys = TAB_QUERY_KEYS[tab] || []
  const result: Record<string, string> = {}
  keys.forEach((key) => {
    const value = queryValueToString(query[key])
    if (value !== '') {
      result[key] = value
    }
  })
  return result
}

export function buildQueryForTab(
  tab: string,
  slice: Record<string, string>,
): Record<string, string> {
  const query: Record<string, string> = { tab }
  const keys = TAB_QUERY_KEYS[tab] || []
  keys.forEach((key) => {
    const value = slice[key]
    if (value != null && value !== '') {
      query[key] = value
    }
  })
  return query
}

export function areQueryRecordsEqual(
  a: Record<string, string>,
  b: Record<string, string>,
): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every((key) => a[key] === b[key])
}
